import {
  AnyConferenceChangedByOtherEvent,
  ConferenceCreatedByOtherEvent,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  NotificationIncomingEvent,
  PServed,
  HistoryMessageItem,
  MessageCallMissed,
  MessageCallTerminated,
  MessageCallEndedInConference,
  useIM,
} from '@wui/im'


import { getSystemMessageText } from '@/entities/chat-system-message'
import { useStoreContacts } from '@/entities/contact'
import { chatModel, isCurrentUserMentionedOrQuotedAtMessage, resolveForwardingAuthorName } from '@/entities/messages'
import { loadMessage, useNotInHistoryMessagesStore } from '@/entities/not-in-history-messages'
import { useStoreSettingsNotifications } from '@/entities/notification'
import { useStoreRooms, useNotificationPreferenceStore } from '@/entities/room'
import { RTCSessionFacade } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { ServiceWorkerNotification } from '@/shared/services/browser-notification'
import { isWindowHidden } from '@/shared/services/window-visibility'
import { TypesOfNotification } from '@/shared/types/notification'
import { OSNotificationOptions, OSNotificationEventType, IUserNotification } from '@/shared/types/os-notification'
import { OwnStatus } from '@/shared/types/store'
import { convertTimestampToDateTimeString } from '@/shared/utils/date-time'
import { isGuestIdentity, resolveGuestDisplayName } from '@/shared/utils/guest'
import { buildMessageNotificationBody } from '@/shared/utils/notification-message-body'
import { fromPServedUser, pServedIsRoom } from '@/shared/utils/uri'
import { getAbsoluteURL } from '@/shared/utils/url'

const notifications: Array<IUserNotification> = []

const resolveNotificationActorName = (roomPServed: PServed, executor: string | undefined, fallback: string): string => {
  const contactName = executor ? useStoreContacts().findByPServed(executor)?.name : undefined
  if (contactName) {
    return contactName
  }

  if (!isGuestIdentity(executor)) {
    return fallback
  }

  return resolveGuestDisplayName(roomPServed, executor) ?? fallback
}

export function useNotification () {
  const { isInDontDisturbGroups } = useStoreSettingsNotifications()

  const createNotification = async <T extends OSNotificationEventType>(title: string, options: OSNotificationOptions<T>) => {
    return isWindowHidden().then((hidden) => {
      if (!hidden) {
        return
      }
      try {
        const notification: IUserNotification = new ServiceWorkerNotification(title, options)
        notifications.push(notification)
        notification.showNotification()
      } catch (e) {
        console.warn(e)
      }
    })
  }

  const sendIncomingMessageNotification = async (from: PServed, roomId: string, message: NotificationIncomingEvent | MessageIncomingEvent) => {
    if (message.type === 'IncomingMessageEvent') {
      const { t } = useLocalization()
      const { currentUser } = useCurrentUser()
      const { findByPServed } = useStoreRooms()

      const room = findByPServed(from)
      const fromName = resolveNotificationActorName(from, message.executor, t('common.unknownContact'))

      let title = room?.isGroup ? t('common.newMessageFromGroup', { from: fromName, chat: room?.name }) : t('common.newMessageFrom', { from: fromName })
      const quotedEventId = message.quote?.quotedEventId
      if (quotedEventId) {
        const { findById } = chatModel.useChatMessageStore()
        const { getMessageByEventId, addMessage } = useNotInHistoryMessagesStore()

        let event = findById(quotedEventId)

        if (!event) {
          event = getMessageByEventId(quotedEventId)
          if (!event) {
            event = await loadMessage(from, quotedEventId)
          }
          if (event?.eventId) {
            addMessage(event.eventId, event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>)
          }
        }

        if (event && event.item.type === 'OutgoingMessageEvent') {
          title = room?.isGroup ? t('common.userQuoteInChatYou', { name: fromName, chat: room?.name }) : t('common.userQuoteYou', { name: fromName })
        }
      }
      const mentionedUsers = message.mentionedUsers
      if (currentUser.value?.imLogin && mentionedUsers?.includes(currentUser.value?.imLogin)) {
        title = t('common.userMentionYou', { name: fromName, chat: room?.name })
      }

      const { body, imageRelativePath } = buildMessageNotificationBody(message, t, {
        resolveForwardingAuthorName,
      })
      const incomingOptions: OSNotificationOptions<'incomingMessage'> = {
        body,
        tag: message.eventId.toString(),
        data: {
          roomId,
          pServed: from,
          eventId: message.eventId,
          rank: message.rank,
          type: 'incomingMessage',
        },
        actions: [
          { title: t('common.markAsRead'), action: 'markAsRead' },
          { title: '👍', action: 'markAsReadWithThumbsUp' },
          { title: '❤️', action: 'markAsReadWithLike' },
        ],
      }
      if (imageRelativePath) {
        incomingOptions.image = getAbsoluteURL(imageRelativePath)
      }

      createNotification(title, incomingOptions)
    }
  }

  const createIncomingMessageNotification = async (from: PServed, roomId: string, message: MessageIncomingEvent) => {
    const { ownStatus } = useStoreContacts()
    const { currentUser } = useCurrentUser()

    const { getRoomById } = useStoreRooms()
    const room = getRoomById(roomId)
    const disabled = useNotificationPreferenceStore().getAccessByRoomId(from)
    const isCurrentUserNotMentionedAndNotQuoted = !isCurrentUserMentionedOrQuotedAtMessage(message)

    if (disabled && isCurrentUserNotMentionedAndNotQuoted) {
      // ignore
    } else if (ownStatus === OwnStatus.DO_NOT_DISTURB) {
      if (message.quote?.quotedEventId && isInDontDisturbGroups(TypesOfNotification.MENTION_AND_QUOTE)) {
        return sendIncomingMessageNotification(from, roomId, message)
      }

      if (currentUser.value?.imLogin && message.mentionedUsers?.includes(currentUser.value?.imLogin) && isInDontDisturbGroups(TypesOfNotification.MENTION_AND_QUOTE)) {
        return sendIncomingMessageNotification(from, roomId, message)
      }

      if (room?.isGroup && isInDontDisturbGroups(TypesOfNotification.GROUP_CHATS_MESSAGE)) {
        return sendIncomingMessageNotification(from, roomId, message)
      }

      if (!room?.isGroup && isInDontDisturbGroups(TypesOfNotification.P2P_CHATS_MESSAGE)) {
        return sendIncomingMessageNotification(from, roomId, message)
      }
    } else {
      return sendIncomingMessageNotification(from, roomId, message)
    }
  }

  const resolveRoomDisplayName = async (pServed: PServed): Promise<string> => {
    const { findByPServed } = useStoreRooms()
    const roomId = fromPServedUser(pServed)
    const room = findByPServed(pServed)

    if (room?.name && room.name !== roomId) {
      return room.name
    }

    try {
      const { name } = await useIM().requestBriefStatus({ contactId: pServed })
      return name || room?.name || roomId
    } catch (e) {
      console.warn(e)
      return room?.name || roomId
    }
  }

  const createYouAreAddedToRoomNotification = (event: ConferenceCreatedByOtherEvent) => {
    const name = useStoreContacts().findByPServed(event.executor)?.name ?? ''
    const { ownStatus } = useStoreContacts()

    if (ownStatus === OwnStatus.DO_NOT_DISTURB && !isInDontDisturbGroups(TypesOfNotification.ROOM_CREATING)) {
      return
    }

    const { t } = useLocalization()

    return createNotification(t('common.chatWasCreatedBy', { author: name, name: event.name }), {
      body: '',
      tag: event.eventId.toString(),
      data: {
        roomId: fromPServedUser(event.pServed),
        type: 'roomChanged',
      },
    })
  }

  const createAnyConferenceChangedByOtherEventNotification = async (event: AnyConferenceChangedByOtherEvent) => {
    const { ownStatus } = useStoreContacts()

    if (ownStatus === OwnStatus.DO_NOT_DISTURB && !isInDontDisturbGroups(TypesOfNotification.ROOM_CREATING)) {
      return
    }

    const notificationOptions: OSNotificationOptions<'roomChanged'> = {
      body: '',
      tag: event.eventId.toString(),
      data: {
        roomId: fromPServedUser(event.pServed),
        type: 'roomChanged',
      },
    }

    const name = await resolveRoomDisplayName(event.pServed)
    let executorName = ''
    const title = getSystemMessageText({
      item: event,
      eventId: 0,
      type: 0,
      status: 0,
      rank: 0,
      timestamp: 0,
    })

    if (event.executor) {
      executorName = useStoreContacts().findByPServed(event.executor)?.name ?? ''
    }

    const resultTitle = `${name}\n${executorName} ${title}`
    return createNotification(resultTitle, notificationOptions)
  }

  const closeNewIncomingCallNotification = (sessionId: RTCSessionFacade['sessionId']) => {
    closeNotification(`incomingCall-${sessionId}`)
  }

  const createNewIncomingCallNotification = (
    from: string,
    sessionId: RTCSessionFacade['sessionId'],
    isGroup: boolean,
  ) => {
    const { t } = useLocalization()
    const { ownStatus } = useStoreContacts()

    if (ownStatus === OwnStatus.DO_NOT_DISTURB) {
      if (isGroup && !isInDontDisturbGroups(TypesOfNotification.GROUP_CHATS_CALL)) {
        return
      }
      if (!isGroup && !isInDontDisturbGroups(TypesOfNotification.P2P_CALL)) {
        return
      }
    }

    return createNotification(t('common.incomingCall'), {
      body: t('common.incomingCallFrom', { from }),
      tag: `incomingCall-${sessionId}`,
      data: {
        type: 'incomingCall',
        sessionId,
      },
      requireInteraction: true,
      actions: [
        { title: t('common.toAnswer'), action: 'callAnswer' }, { title: t('common.toReject'), action: 'callReject' },
      ],
    })
  }

  const createCallMissedNotification = (message: MessageCallMissed | MessageCallTerminated | MessageCallEndedInConference) => {
    const { t } = useLocalization()
    const { ownStatus } = useStoreContacts()

    const roomPServed = pServedIsRoom(message.pServed)
      ? message.pServed
      : pServedIsRoom(message.dstPServed)
        ? message.dstPServed
        : pServedIsRoom(message.srcPServed)
          ? message.srcPServed
          : undefined

    const isGroup = Boolean(roomPServed) || ('roomName' in message)

    if (ownStatus === OwnStatus.DO_NOT_DISTURB) {
      const shouldNotifyInDnd = isGroup
        ? isInDontDisturbGroups(TypesOfNotification.GROUP_CHATS_CALL)
        : isInDontDisturbGroups(TypesOfNotification.P2P_CALL)

      if (!shouldNotifyInDnd) {
        return
      }
    }

    if (isGroup) {
      const roomIdForPreference = roomPServed ?? message.pServed
      const disabled = useNotificationPreferenceStore().getAccessByRoomId(roomIdForPreference)
      if (disabled) {
        return
      }
      const { findByPServed } = useStoreRooms()
      const roomName = 'roomName' in message ? message.roomName : findByPServed(roomPServed ?? message.pServed)?.name ?? ''
      const roomId = fromPServedUser(roomPServed ?? message.pServed)

      const title = roomName ? `${t('chat.youMissedCall')} — ${roomName}` : t('chat.youMissedCall')
      return createNotification(title, {
        body: convertTimestampToDateTimeString(message.timestamp),
        tag: `callMissed-${message.eventId}`,
        data: {
          type: 'callMissed',
          roomId,
          roomName,
          isGroup: true,
        },
      })
    }

    const targetNumber = fromPServedUser(message.srcPServed ?? '')
    return createNotification(t('chat.youMissedCall'), {
      body: `${convertTimestampToDateTimeString(message.timestamp)} ${message.srcName} ${targetNumber}`,
      tag: `callMissed-${message.eventId}`,
      data: {
        type: 'callMissed',
        targetNumber,
        isGroup: false,
      },
      actions: [
        { title: t('common.makeCallBack'), action: 'makeCall' },
      ],
    })
  }

  const closeConferenceStartedNotification = (pServed: PServed) => {
    closeNotification(`conferenceStarted-${pServed}`)
  }

  const createConferenceStartedNotification = (
    pServed: PServed,
    from: string,
  ) => {
    const disabled = useNotificationPreferenceStore().getAccessByRoomId(pServed)
    if (disabled) {
      return
    }

    const { ownStatus } = useStoreContacts()

    if (ownStatus === OwnStatus.DO_NOT_DISTURB && !isInDontDisturbGroups(TypesOfNotification.GROUP_CHATS_CALL)) {
      return
    }

    const { t } = useLocalization()
    return createNotification(t('common.callStarted'), {
      body: from,
      tag: `conferenceStarted-${pServed}`,
      data: {
        type: 'conferenceStarted',
        pServed,
      },
      requireInteraction: true,
      actions: [
        { title: t('common.toConnect'), action: 'join' },
      ],
    })
  }

  const closePickupCallNotification = (callId: string) => {
    closeNotification(`pickup-${callId}`)
  }

  const createPickupCallNotification = (from: string, to: string, targetNumber: string, callId: string) => {
    const { t } = useLocalization()
    return createNotification(t('callsSettings.subscriberPickUp'), {
      body: t('common.subscriberPickUpBodyDialog', { from, to }),
      tag: `pickup-${callId}`,
      data: {
        type: 'pickup',
        targetNumber,
        callId,
      },
      actions: [
        { title: t('callsSettings.subscriberPickUp'), action: 'pickupAnswer' },
      ],
      silent: true,
    })
  }

  const closeNotification = (id?: string) => {
    if (id) {
      notifications.find((n) => id === n.getId())?.close()
    }
  }

  const closeAllNotifications = () => {
    notifications.forEach((n) => n.close())
  }

  return {
    createIncomingMessageNotification,
    createCallMissedNotification,
    createYouAreAddedToRoomNotification,
    createNewIncomingCallNotification,
    closeNewIncomingCallNotification,
    createConferenceStartedNotification,
    createAnyConferenceChangedByOtherEventNotification,
    createPickupCallNotification,
    closeConferenceStartedNotification,
    closePickupCallNotification,
    closeAllNotifications,
  }
}
